import { useState, useEffect } from "react"
import { LogOut, ShieldAlert } from "lucide-react"
import PulseLogo from "@/components/PulseLogo"
import { getAuthorityStatus } from "@/lib/api"

interface Props {
  status: { status: string; issues: string[] } | null
  email: string
  onClickIssues?: () => void
  onLogout?: () => void
}

const statusColors: Record<string, string> = {
  healthy: "#22c55e",
  warn: "#f59e0b",
  critical: "#ef4444",
}

export default function StatusBar({ status, email, onClickIssues, onLogout }: Props) {
  const s = status?.status || "unknown"
  const issueCount = status?.issues?.length || 0
  const [confirming, setConfirming] = useState(false)
  const [pendingT2, setPendingT2] = useState(0)
  const color = statusColors[s] || "#6b7280"

  useEffect(() => {
    const load = () => {
      getAuthorityStatus()
        .then(data => setPendingT2(data.summary?.pending_tier2 || 0))
        .catch(() => {})
    }
    load()
    const id = setInterval(load, 15000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex items-center px-4 py-2.5 gap-3 border-b border-border bg-surface safe-top">
      {/* Pulse branding — status color ring wraps logo + name */}
      <div className="relative flex items-center">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: `1.5px solid ${color}`,
            boxShadow: `0 0 8px ${color}50, 0 0 16px ${color}20`,
            animation: "statusGlow 2s ease-in-out infinite",
          }}
        />
        <div className="relative flex items-center gap-2 px-2.5 py-1">
          <PulseLogo size={22} className="shrink-0" />
          <span className="text-sm font-semibold text-white">Pulse</span>
        </div>
      </div>

      <div className="flex-1" />

      {pendingT2 > 0 && (
        <div className="flex items-center gap-1 text-xs text-amber-400 font-mono animate-pulse">
          <ShieldAlert size={12} />
          <span>{pendingT2} pending</span>
        </div>
      )}

      {issueCount > 0 && (
        <button
          onClick={onClickIssues}
          className="text-xs text-pulse-yellow font-mono cursor-pointer hover:text-yellow-300 transition-colors"
        >
          {issueCount} issue{issueCount > 1 ? "s" : ""}
        </button>
      )}

      {email && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 hidden sm:inline">
            {email.split("@")[0]}
          </span>
          {onLogout && !confirming && (
            <button
              onClick={() => setConfirming(true)}
              className="text-gray-500 hover:text-gray-300 transition-colors"
              title="Log out of Pulse"
            >
              <LogOut size={14} />
            </button>
          )}
          {onLogout && confirming && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setConfirming(false); onLogout() }}
                className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                Log out
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="text-[10px] text-gray-500 hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
